import { useCallback, useState } from 'react';

const PREFIX = 'cb-session-state:';

const identity = value => value;

const read = (key, defaultValue, deserialize) => {
  if (!key) return defaultValue;
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    return raw === null ? defaultValue : deserialize(JSON.parse(raw));
  } catch (e) {
    return defaultValue;
  }
};

const write = (key, value) => {
  if (!key) return;
  try {
    if (value === undefined) {
      sessionStorage.removeItem(PREFIX + key);
    } else {
      sessionStorage.setItem(PREFIX + key, JSON.stringify(value));
    }
  } catch (e) {
    // storage full or blocked; state still works in memory
  }
};

// Removes every value saved by useSessionState. Called on logout.
export const clearSessionState = () => {
  try {
    Object.keys(sessionStorage)
      .filter(key => key.startsWith(PREFIX))
      .forEach(key => sessionStorage.removeItem(key));
  } catch (e) {
    // storage blocked; nothing to clear
  }
};

/**
 * useState that survives navigation and refresh for the life of the browser tab.
 * A null key (e.g. before the user id is known) behaves like plain useState.
 * Values are stored as JSON; pass `deserialize` to rebuild Dates / dayjs objects.
 */
const useSessionState = (key, defaultValue, { deserialize = identity } = {}) => {
  const [state, setState] = useState(() => ({ key, value: read(key, defaultValue, deserialize) }));

  let { value } = state;
  if (state.key !== key) {
    // Key changed (e.g. user id arrived after a refresh): load that key's saved value.
    value = read(key, defaultValue, deserialize);
    setState({ key, value });
  }

  const setValue = useCallback(next => {
    setState(prev => {
      const nextValue = typeof next === 'function' ? next(prev.value) : next;
      write(prev.key, nextValue);
      return { key: prev.key, value: nextValue };
    });
  }, []);

  return [value, setValue];
};

export default useSessionState;
